import { FC, useState } from "react";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../../api/api";
import { orderApi } from "../../../api/order.api";
import { icons } from "../../../icons/IconSet";
import { IOrder, IRefundItem, IRefundRequest, IRefundSaveResponse } from "../../../model/IOrder";
import Modal from "../../common/Modal";
import Button from "../../form/Button";
import { DetailPanel, DetailTable } from "../details/DetailPanel";
import RefundForm from "./RefundForm";

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? null : `$${value.toFixed(2)}`;

// --
// -- Rules on both sides of every cell, matching the header row. The outer
// -- edges are dropped so the table keeps its open sides rather than gaining
// -- a frame.
// --
// -- Values sit left, like the column titles above them: the amounts used to
// -- be right-aligned under left-aligned headings, which read as broken.
// --
const CELL = 'py-2 px-2 border-x-[1px] border-x-gray-200 first:border-l-0 last:border-r-0';

/**
 * What QuickBooks has for a row, in a few words.
 *
 * The stale case is the one that needs saying out loud: a refund posted and
 * then edited here is never posted again, so QBO still holds the old amounts.
 */
const QboState: FC<{ refund: IRefundItem }> = ({ refund }) => {
  if (refund.qboStaleAfterEdit) return (
    <span className="text-amber-700 whitespace-nowrap" title={`QuickBooks still holds the amounts from before this edit (journal entry ${refund.qboJournalEntryId}). Amend it there.`}>
      Edited after posting
    </span>
  );
  if (refund.qboJournalEntryId) return (
    <span className="text-text_pale whitespace-nowrap" title={`Journal entry ${refund.qboJournalEntryId}`}>
      In QuickBooks
    </span>
  );
  if (refund.qboSyncStatus === 'FAILED') return (
    <span className="text-pink-600 whitespace-nowrap" title={refund.qboSyncError || ''}>
      Not posted
    </span>
  );
  return <span className="text-text_pale whitespace-nowrap">—</span>;
};

const RefundSection: FC<{ order: IOrder, vendor: boolean, canEdit: boolean }> = ({
  order,
  vendor,
  canEdit,
}) => {
  const rows = (order.refundItems || []).filter((r) => !!r.isVendorRefund === vendor);

  const [editing, setEditing] = useState<IRefundItem | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  const [addRefund, { isLoading: isCreating }] = orderApi.useAddRefundMutation();
  const [updateRefund, { isLoading: isUpdating }] = orderApi.useUpdateRefundMutation();
  const isSaving = isCreating || isUpdating;

  const close = () => {
    setIsAdding(false);
    setEditing(null);
  };

  // --
  // -- The save succeeded even when QuickBooks did not. Saying "saved, not in
  // -- QuickBooks yet" is the honest message; calling it a failure would have
  // -- people enter the refund twice.
  // --
  const report = (result: IRefundSaveResponse, what: string) => {
    if (result.pushError) {
      toast.success(`${what}. Not sent to QuickBooks yet: ${result.pushError}`, { duration: 8000 });
      return;
    }
    if (result.push?.failed) {
      toast.success(`${what}. QuickBooks rejected ${result.push.failed} of this order's refunds.`,
        { duration: 8000 });
      return;
    }
    toast.success(result.push?.created
      ? `${what} and sent to QuickBooks.`
      : `${what}.`);
  };

  const handleSubmit = async (request: IRefundRequest) => {
    try {
      const result = editing?.id
        ? await updateRefund({ orderId: order.orderId, refundId: editing.id, ...request }).unwrap()
        : await addRefund({ orderId: order.orderId, ...request }).unwrap();

      report(result, editing ? 'Refund updated' : 'Refund added');
      close();
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Failed to save the refund');
    }
  };

  const title = vendor ? 'Vendor Refund Info' : 'Refund Info';

  return (
    <DetailPanel
      title={title}
      actions={canEdit ? (
        <Button
          isNotFullWidth
          size="small"
          variant="outline"
          className="flex items-center gap-1 whitespace-nowrap"
          onClick={() => { setEditing(null); setIsAdding(true); }}
        >
          {icons('plus', { width: '14px' })}
          {vendor ? 'Add Vendor Refund' : 'Add Refund'}
        </Button>
      ) : undefined}
    >
      <DetailTable
        bordered
        head={['Type', 'Part', 'Freight', 'Shipping Protection', 'Tax', 'Vendor Code', 'QuickBooks',
          ...(canEdit ? [''] : [])]}
        empty={vendor ? 'No vendor refunds' : 'No refunds or chargebacks'}
      >
        {rows.map((refund, i) => [
          <tr key={`r${refund.id ?? i}`} className="odd:bg-gray-50">
            <td className={`${CELL} whitespace-nowrap font-medium`}>{refund.type || '—'}</td>
            <td className={CELL}>{money(refund.partPrice) ?? '—'}</td>
            <td className={CELL}>{money(refund.freight) ?? '—'}</td>
            <td className={CELL}>{money(refund.automotiveCare) ?? '—'}</td>
            <td className={CELL}>{money(refund.taxes) ?? '—'}</td>
            <td className={`${CELL} whitespace-nowrap`}>{refund.supplierKey || '—'}</td>
            <td className={CELL}><QboState refund={refund} /></td>
            {canEdit && (
              <td className={`${CELL} text-right`}>
                <Button
                  isNotFullWidth
                  size="small"
                  variant="outline"
                  disabled={!refund.id}
                  onClick={() => { setIsAdding(false); setEditing(refund); }}
                >
                  {icons('edit-record', { width: '14px' })}
                </Button>
              </td>
            )}
          </tr>,
          // The old page gave comments their own full width row under each record.
          refund.comments
            ? (
              <tr key={`c${refund.id ?? i}`} className="odd:bg-gray-50">
                <td colSpan={canEdit ? 8 : 7} className="pb-3 px-2 text-text_pale">{refund.comments}</td>
              </tr>
            )
            : null,
        ])}
      </DetailTable>

      {/* Mounted only while open, so the form seeds from the row it was
          opened on rather than holding a stale draft. */}
      {(isAdding || !!editing) && (
        <Modal
          isShown
          title={editing
            ? `Edit ${vendor ? 'Vendor Refund' : 'Refund'} #${editing.id}`
            : `New ${vendor ? 'Vendor Refund' : 'Refund'}`}
          style={{ maxWidth: '720px', overflowY: 'visible' }}
          onClose={close}
        >
          <RefundForm
            order={order}
            refund={editing}
            isVendorRefund={vendor}
            loading={isSaving}
            onSubmit={handleSubmit}
          />
        </Modal>
      )}
    </DetailPanel>
  );
};

export default RefundSection;
